import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

/**
 * Per-lever contribution waterfall (multi-lever scenarios).
 *
 * The backend re-runs the champion with each lever ALONE (leave-one-in) and
 * stores per-lever end-of-horizon deltas plus the interaction residual —
 * this renders that decomposition as a classic waterfall: baseline end →
 * one floating step per lever → interaction → scenario end. Pure SVG,
 * same visual language as the fan chart.
 */

const UP_COLOR = '#ff9800';
const DOWN_COLOR = '#66bb6a';

const fmtNum = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1000) return v.toFixed(0);
  if (a >= 10) return v.toFixed(2);
  return v.toFixed(4);
};

// Long driver keys ("price.power_spot__ERCOT") stay readable in a narrow
// column: keep the tail (the discriminating part) and elide the front.
const shortLabel = (s, max = 14) => {
  const t = String(s || '');
  return t.length <= max ? t : `…${t.slice(t.length - max + 1)}`;
};

const ScenarioWaterfall = ({
  attribution, baselineEnd, unit = null, probability = false, height = 200,
}) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(560);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fmtVal = (v) => (probability
    ? `${(v * 100).toFixed(1)}%`
    : fmtNum(v));
  const fmtDelta = (v) => {
    if (probability) {
      const pts = Math.round(v * 1000) / 10;
      return `${pts >= 0 ? '+' : ''}${pts} pts`;
    }
    return `${v >= 0 ? '+' : ''}${fmtNum(v)}`;
  };

  const steps = useMemo(() => {
    const contribs = attribution?.contributions || [];
    if (!contribs.length || baselineEnd == null) return null;
    const out = [{ kind: 'total', label: 'Baseline', value: baselineEnd }];
    let running = baselineEnd;
    contribs.forEach((c) => {
      const d = Number(c.end_delta || 0);
      out.push({
        kind: 'delta',
        label: c.requested_key || c.key,
        sub: c.kind === 'event' ? 'event' : null,
        delta: d,
        from: running,
        to: running + d,
      });
      running += d;
    });
    const inter = Number(attribution?.interaction?.end_delta || 0);
    // The residual is only worth a column when it is visible next to the
    // lever moves (>2% of the largest lever) — linear models leave ~0.
    const maxLever = Math.max(...contribs.map(
      (c) => Math.abs(Number(c.end_delta || 0))), 0);
    if (Math.abs(inter) > Math.max(maxLever * 0.02, 1e-9)) {
      out.push({
        kind: 'delta', label: 'Interaction', sub: 'combined effect',
        delta: inter, from: running, to: running + inter,
      });
      running += inter;
    }
    out.push({ kind: 'total', label: 'Scenario', value: running });
    return out;
  }, [attribution, baselineEnd]);

  if (!steps) return null;

  const M = { top: 22, right: 12, bottom: 34, left: 56 };
  const W = Math.max(width, 320);
  const innerW = W - M.left - M.right;
  const innerH = height - M.top - M.bottom;

  const levels = steps.flatMap((s) => (
    s.kind === 'total' ? [s.value] : [s.from, s.to]
  ));
  let yMin = Math.min(...levels);
  let yMax = Math.max(...levels);
  if (yMin === yMax) { yMin -= 1; yMax += 1; }
  const pad = (yMax - yMin) * 0.15;
  yMin -= pad; yMax += pad;
  const y = (v) => M.top + innerH * (1 - (v - yMin) / (yMax - yMin));

  const slot = innerW / steps.length;
  const barW = Math.min(Math.max(slot * 0.55, 18), 64);
  const cx = (i) => M.left + slot * i + slot / 2;

  const yTicks = [yMin + pad, (yMin + yMax) / 2, yMax - pad];

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      <svg width={W} height={height} style={{ display: 'block' }}>
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} stroke="currentColor" opacity={0.08} />
            <text x={M.left - 8} y={y(v) + 4} textAnchor="end" fontSize={10} fill="currentColor" opacity={0.55}>
              {fmtVal(v)}
            </text>
          </g>
        ))}
        {steps.map((s, i) => {
          const xLeft = cx(i) - barW / 2;
          if (s.kind === 'total') {
            const top = y(s.value);
            return (
              <g key={`${s.label}-${i}`}>
                <rect
                  x={xLeft} y={Math.min(top, height - M.bottom)}
                  width={barW} height={Math.max(height - M.bottom - top, 1.5)}
                  fill="currentColor" opacity={0.28} rx={2}
                />
                <text x={cx(i)} y={top - 6} textAnchor="middle" fontSize={10.5} fontWeight={600} fill="currentColor" opacity={0.85}>
                  {fmtVal(s.value)}
                </text>
                <text x={cx(i)} y={height - M.bottom + 14} textAnchor="middle" fontSize={10.5} fill="currentColor" opacity={0.7}>
                  {s.label}
                </text>
              </g>
            );
          }
          const up = s.delta >= 0;
          const yTop = y(Math.max(s.from, s.to));
          const h = Math.max(Math.abs(y(s.from) - y(s.to)), 1.5);
          const color = up ? UP_COLOR : DOWN_COLOR;
          const prevX = cx(i - 1) + barW / 2;
          return (
            <g key={`${s.label}-${i}`}>
              {/* connector from the previous column's running level */}
              <line
                x1={prevX} x2={xLeft} y1={y(s.from)} y2={y(s.from)}
                stroke="currentColor" opacity={0.25} strokeDasharray="2 2"
              />
              <rect x={xLeft} y={yTop} width={barW} height={h} fill={color} opacity={0.85} rx={2}>
                <title>{`${s.label}: ${fmtDelta(s.delta)}${unit && !probability ? ` ${unit}` : ''}`}</title>
              </rect>
              <text x={cx(i)} y={yTop - 6} textAnchor="middle" fontSize={10.5} fontWeight={600} fill={color}>
                {fmtDelta(s.delta)}
              </text>
              <text x={cx(i)} y={height - M.bottom + 14} textAnchor="middle" fontSize={10.5} fill="currentColor" opacity={0.7}>
                <title>{s.label}</title>
                {shortLabel(s.label)}
              </text>
              {s.sub && (
                <text x={cx(i)} y={height - M.bottom + 26} textAnchor="middle" fontSize={9} fill="currentColor" opacity={0.45}>
                  {s.sub}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
        <Typography variant="caption" color="text.disabled">
          Each lever simulated alone (leave-one-in)
          {unit && !probability ? ` · ${unit}` : ''}
        </Typography>
      </Stack>
    </Box>
  );
};

export default ScenarioWaterfall;
